import { Bot } from "lucide-react";
import {
  PageHeading, Section, Steps, Callout, RoleBadge,
  Diagram, MockField, MockSelect, MockButton,
} from "../shared";

export default function AgentApiSection() {
  return (
    <div data-testid="guide-agent-api">
      <PageHeading
        icon={Bot}
        title="Agent API (Jeeves)"
        description="Outside tools — AI assistants, chatbots, sync robots — can talk to the system through a secure web connection. This page is the integrator's reference: how the keys work, how to send them, and what gets recorded."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">An IT Admin creates the keys. The tool's developer uses them.</span>
      </div>

      <Callout variant="info" title="In plain words">
        An API is just a doorway that lets one computer program ask another for information or to make a change. A key is the pass that opens the doorway. Here you give a tool a key, the tool sends it with every request, and the system checks it.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          When you're connecting an outside program to the system — for example an AI assistant that summarises tasks, or a robot that creates items in bulk. The IT Admin makes the key; the developer plugs it into their tool.
        </p>
      </Section>

      <Section title="What a key looks like">
        <p className="text-sm text-white/65 leading-relaxed">
          Keys take the form <span className="font-mono text-white">keyId.secret</span>. The first part (keyId) is a short tag used to look the key up quickly; only the second part (secret) is scrambled and stored safely. The full key is shown <span className="text-white">once</span> when it's created — store it somewhere safe, because it can't be recovered later.
        </p>
        <p className="text-sm text-white/65 leading-relaxed">Each key carries:</p>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1 ml-2">
          <li><span className="text-white">Name / description</span> — what or who the key is for.</li>
          <li><span className="text-white">Expiry</span> (optional) — a date after which it stops working.</li>
          <li><span className="text-white">Allowed scopes</span> — exactly what it's allowed to do (read tasks, write progress, manage sub-tasks, etc.).</li>
          <li><span className="text-white">Last used</span> — filled in automatically.</li>
          <li><span className="text-white">Revoked at</span> — set when the key is cancelled.</li>
        </ul>
      </Section>

      <Section title="Creating a key">
        <Diagram
          frameLabel="Settings → Agent API Keys"
          legend={[
            <>Give the key a name.</>,
            <>Choose its allowed scopes (and an optional expiry).</>,
            <>Press <span className="text-white">Create Key</span>, then re-enter your own password.</>,
          ]}
        >
          <MockField label="Name / description" value="Summary assistant" marker={1} />
          <MockSelect label="Allowed scopes" value="tasks:read, summary:read" marker={2} />
          <MockButton label="Create Key" marker={3} />
        </Diagram>
        <Steps
          steps={[
            <>Open <span className="text-white">Settings → Agent API Keys</span>.</>,
            <>Press <span className="text-white">Create Key</span>.</>,
            <>Give it a name, an optional expiry, and pick the allowed scopes.</>,
            <>Re-enter your own password to confirm.</>,
            <>Copy the <span className="font-mono text-white">keyId.secret</span> shown once and store it in your tool's secret store.</>,
          ]}
        />
      </Section>

      <Section title="Using a key">
        <p className="text-sm text-white/65 leading-relaxed">
          The tool sends the key as a header on every request:
        </p>
        <pre className="bg-black/40 border border-white/10 rounded-md p-3 text-xs text-cyan-200 font-mono overflow-x-auto">
{`Authorization: Bearer keyId.secret`}
        </pre>
        <p className="text-sm text-white/65 leading-relaxed">
          All agent endpoints live under <span className="font-mono text-white">/api/agent/*</span>. They include creating and reading tasks, posting progress updates, handoffs, status changes, sub-task management, and lookups for people, lanes, and summary.
        </p>
      </Section>

      <Section title="What gets recorded">
        <p className="text-sm text-white/65 leading-relaxed">
          Every successful or failed connection attempt is logged as structured data tagged <span className="font-mono text-white">[Agent Audit]</span> — time, address visited, method, source IP, keyId, and reason. Secrets are never logged.
        </p>
        <p className="text-sm text-white/65 leading-relaxed">
          Anything an outside tool does to an action item gets the note <span className="text-white">"(via Agent API)"</span> next to who did it, so you can always tell automated activity apart from people clicking in the app.
        </p>
      </Section>

      <Callout variant="warning" title="Cancel it immediately if it leaks">
        If you think a key has been exposed, cancel it from Settings → Agent API Keys. Cancelling is instant and can't be undone — the key fails with HTTP 401 on its very next request.
      </Callout>
    </div>
  );
}
